import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { brandContent } from '../content/brand';
import { Lang, DEFAULT_LANG, isLang } from '../i18n/locales';
import { getLangPath } from '../types';
import { t as tHelper, getLocalizedText as textHelper } from '../lib/i18nHelper';

interface LanguageContextType {
  language: Lang;
  setLanguage: (lang: Lang) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  t: (path: string) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLocalizedText: (entity: any, field: string) => string;
  makePath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const potentialLang = pathSegments[0];

  const language: Lang = isLang(potentialLang) ? potentialLang : DEFAULT_LANG;

  const setLanguage = (newLang: Lang) => {
    if (newLang === language) return;
    const newPath = location.pathname.replace(`/${language}`, `/${newLang}`);
    navigate(newPath + location.search + location.hash);
  };

  const t = useMemo(() => (path: string) => tHelper(path, language), [language]);
  const getLocalizedText = useMemo(
    () => (entity: any, field: string) => textHelper(entity, field, language),
    [language]
  );
  const content = useMemo(() => brandContent[language] || brandContent[DEFAULT_LANG], [language]);

  const makePath = (path: string) => getLangPath(path, language);

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, content, t, getLocalizedText, makePath }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
